/**
 * Cohort-aware testing-day math.
 *
 * Two windows per the v3 build plan:
 *   - Staff (Tester Type Admin or Staff): 7-day default, extendable via app_config
 *   - Volunteer: 14-day default
 *
 * All math operates on calendar dates in the *tester's local timezone*. We
 * deliberately do NOT use UTC midnight — testers care about their wearable's
 * sense of "yesterday," which is local. This means a tester crossing
 * timezones during the window may see a stale "Day N" briefly; acceptable
 * tradeoff for the simpler mental model.
 */

import { differenceInCalendarDays, format, parseISO } from "date-fns";

export type TesterType = "Admin" | "Staff" | "Volunteer";

export interface WindowConfig {
  /** ISO date string from app_config.staff_start_date */
  staffStartDate: string;
  /** Default 7. Editable in admin Config. */
  staffWindowDays: number;
  /** ISO date string from app_config.volunteer_start_date */
  volunteerStartDate: string;
  /** Default 14. */
  volunteerWindowDays: number;
}

export interface DayInfo {
  /** 1-based current day. May be < 1 (not yet started) or > windowDays (complete). */
  currentDay: number;
  /** Cohort window length (7 staff / 14 volunteer / extended). */
  windowDays: number;
  /** True if today < start date for this cohort. */
  isBeforeStart: boolean;
  /** True if today > start + windowDays - 1. */
  isComplete: boolean;
  /** Short label, e.g. "Sat, Apr 18". */
  dateShort: string;
  /** Compact composite, e.g. "Day 4 of 7 · Sat, Apr 18". */
  dayLabel: string;
  /** Long composite for missed-days headers, e.g. "Day 3 · Fri, Apr 17". */
  dayLong: string;
}

function startDateFor(type: TesterType, config: WindowConfig): Date {
  return parseISO(
    type === "Volunteer" ? config.volunteerStartDate : config.staffStartDate,
  );
}

function windowDaysFor(type: TesterType, config: WindowConfig): number {
  return type === "Volunteer" ? config.volunteerWindowDays : config.staffWindowDays;
}

export function currentDay(
  testerType: TesterType,
  today: Date,
  config: WindowConfig,
): DayInfo {
  const start = startDateFor(testerType, config);
  const windowDays = windowDaysFor(testerType, config);
  const dayNum = differenceInCalendarDays(today, start) + 1;
  return {
    currentDay: dayNum,
    windowDays,
    isBeforeStart: dayNum < 1,
    isComplete: dayNum > windowDays,
    dateShort: format(today, "EEE, MMM d"),
    dayLabel: `Day ${dayNum} of ${windowDays} · ${format(today, "EEE, MMM d")}`,
    dayLong: `Day ${dayNum} · ${format(today, "EEE, MMM d")}`,
  };
}

/**
 * Which cohorts are active at a given date? Used to scope coverage
 * denominators in the admin dashboard.
 */
export function activeCohortsAt(
  date: Date,
  config: WindowConfig,
): Array<"staff" | "volunteer"> {
  const cohorts: Array<"staff" | "volunteer"> = [];
  const staffInfo = currentDay("Staff", date, config);
  if (!staffInfo.isBeforeStart && !staffInfo.isComplete) cohorts.push("staff");
  const volInfo = currentDay("Volunteer", date, config);
  if (!volInfo.isBeforeStart && !volInfo.isComplete) cohorts.push("volunteer");
  return cohorts;
}

/** Format a calendar date for past-day stack headers, e.g. "Day 3 · Fri, Apr 17". */
export function formatPastDayHeader(
  testerType: TesterType,
  pastDate: Date,
  config: WindowConfig,
): string {
  const info = currentDay(testerType, pastDate, config);
  return info.dayLong;
}
